import { createClient } from '@supabase/supabase-js'

// These environment variables should be set in your Vercel deployment
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://your-project.supabase.co'
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'your-anon-key'

// Create a single supabase client for interacting with your database
export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Database table types
export interface DatabaseUser {
  id: string
  name: string
  email?: string
  email_consent?: boolean
  business_type?: string
  industry?: string
  team_size?: number
  current_ai_experience: 'none' | 'basic' | 'intermediate' | 'advanced'
  primary_goals?: string[]
  created_at: string
  updated_at: string
}

export interface DatabaseUserProgress {
  id: string
  user_id: string
  current_stage: number
  stage_progress: Record<string, any>
  completed_at?: string
  personalized_recommendations?: Record<string, any>[]
  created_at: string
  updated_at: string
}

export interface DatabaseUserInteraction {
  id: string
  user_id: string
  stage_id: number
  interaction_type: string
  interaction_data: Record<string, any>
  time_spent_seconds?: number
  created_at: string
}

export interface DatabaseJourneySession {
  id: string
  user_id: string
  session_start: string
  session_end?: string
  stages_visited: number[]
  total_time_minutes?: number
  completion_status: 'incomplete' | 'completed' | 'abandoned'
  device_info: Record<string, any>
  created_at: string
}

export interface DatabaseGeneratedRecommendation {
  id: string
  user_id: string
  recommendation_type: string
  recommendation_data: Record<string, any>
  match_score?: number
  user_feedback?: string
  created_at: string
}

// Helper functions for database operations
export async function saveUser(userData: {
  name: string
  email?: string
  emailConsent?: boolean
  businessType?: string
  industry?: string
  teamSize?: number
  currentAIExperience: 'none' | 'basic' | 'intermediate' | 'advanced'
  primaryGoals?: string[]
}) {
  const { data, error } = await supabase
    .from('users')
    .insert({
      name: userData.name,
      email: userData.email,
      email_consent: userData.emailConsent,
      business_type: userData.businessType,
      industry: userData.industry,
      team_size: userData.teamSize,
      current_ai_experience: userData.currentAIExperience,
      primary_goals: userData.primaryGoals,
    })
    .select()
    .single()

  if (error) {
    console.error('Error saving user:', error)
    throw error
  }

  return data
}

export async function updateUserProgress(userId: string, progressData: {
  currentStage: number
  stageProgress: Record<string, any>
  personalizedRecommendations?: any[]
}) {
  const { data, error } = await supabase
    .from('user_progress')
    .upsert({
      user_id: userId,
      current_stage: progressData.currentStage,
      stage_progress: progressData.stageProgress,
      personalized_recommendations: progressData.personalizedRecommendations,
      updated_at: new Date().toISOString(),
    })
    .select()
    .single()

  if (error) {
    console.error('Error updating user progress:', error)
    throw error
  }

  return data
}

export async function getUsersWithEmailConsent() {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('email_consent', true)
    .not('email', 'is', null)

  if (error) {
    console.error('Error fetching users with email consent:', error)
    throw error
  }

  return data
}

// Analytics helper functions
export async function trackUserInteraction(interactionData: {
  userId: string
  stageId: number
  interactionType: string
  interactionData?: Record<string, any>
  timeSpentSeconds?: number
}) {
  const { data, error } = await supabase
    .from('user_interactions')
    .insert({
      user_id: interactionData.userId,
      stage_id: interactionData.stageId,
      interaction_type: interactionData.interactionType,
      interaction_data: interactionData.interactionData || {},
      time_spent_seconds: interactionData.timeSpentSeconds,
    })
    .select()
    .single()

  if (error) {
    console.error('Error tracking user interaction:', error)
    throw error
  }

  return data
}

export async function createJourneySession(sessionData: {
  userId: string
  deviceInfo?: Record<string, any>
}) {
  const { data, error } = await supabase
    .from('journey_sessions')
    .insert({
      user_id: sessionData.userId,
      device_info: sessionData.deviceInfo || {},
      completion_status: 'incomplete'
    })
    .select()
    .single()

  if (error) {
    console.error('Error creating journey session:', error)
    throw error
  }

  return data
}

export async function updateJourneySession(sessionId: string, updates: {
  stagesVisited?: number[]
  totalTimeMinutes?: number
  completionStatus?: 'incomplete' | 'completed' | 'abandoned'
}) {
  const { data, error } = await supabase
    .from('journey_sessions')
    .update({
      stages_visited: updates.stagesVisited,
      total_time_minutes: updates.totalTimeMinutes,
      completion_status: updates.completionStatus,
      session_end: updates.completionStatus !== 'incomplete' ? new Date().toISOString() : undefined
    })
    .eq('id', sessionId)
    .select()
    .single()

  if (error) {
    console.error('Error updating journey session:', error)
    throw error
  }

  return data
}

export async function saveGeneratedRecommendation(recommendationData: {
  userId: string
  recommendationType: string
  recommendationData: Record<string, any>
  matchScore?: number
}) {
  const { data, error } = await supabase
    .from('generated_recommendations')
    .insert({
      user_id: recommendationData.userId,
      recommendation_type: recommendationData.recommendationType,
      recommendation_data: recommendationData.recommendationData,
      match_score: recommendationData.matchScore,
    })
    .select()
    .single()

  if (error) {
    console.error('Error saving generated recommendation:', error)
    throw error
  }

  return data
}

// Enhanced analytics queries
export async function getJourneyAnalytics() {
  // Get funnel data
  const { data: users, error: usersError } = await supabase
    .from('users')
    .select('id, created_at')

  const { data: progress, error: progressError } = await supabase
    .from('user_progress')
    .select('user_id, current_stage, stage_progress')

  const { data: interactions, error: interactionsError } = await supabase
    .from('user_interactions')
    .select('user_id, stage_id, interaction_type, time_spent_seconds, created_at')

  if (usersError || progressError || interactionsError) {
    console.error('Error fetching analytics data:', { usersError, progressError, interactionsError })
    throw usersError || progressError || interactionsError
  }

  return { users, progress, interactions }
}

export async function getPrimaryGoalsAnalysis() {
  const { data, error } = await supabase
    .from('users')
    .select('primary_goals, business_type, industry, current_ai_experience')
    .not('primary_goals', 'is', null)

  if (error) {
    console.error('Error fetching primary goals analysis:', error)
    throw error
  }

  return data
}